import React, { useCallback } from "react";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";
import { ReactComponent as WhatsappBorderedIcon } from "./assets/whatsapp-2.svg";
import { CRMAccountCard } from "../CRMAccountCard";
import { CRMAccountTile } from "../CRMAccountTile";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { ReactComponent as RemoveIcon } from "./assets/remove.svg";
import { useWhatsappAccount } from "../../../../providers/WhatsappAccountProvider/useWhatsappAccount";
import defaultAvatar from "./assets/default-avatar.png";
import { useManageAccountsDrawer } from "../../providers/ManageAccountsDrawerContext";

export const WhatsappAccountCard: React.FC<{
  hideReconnectOption?: boolean;
  isClickable?: boolean;
  variant?: "card" | "tile";
}> = ({ hideReconnectOption, isClickable, variant = "card" }) => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { navigateToPage } = useNavigation();
  const { toast } = useToast();
  const {
    whatsappAgent,
    reconnectWhatsappAccount,
    isConnected,
    connectWhatsappAccount,
  } = useWhatsappAccount();
  const { openManageAccountDrawer, isInboxModule } = useManageAccountsDrawer();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();

  const [disconnectWhatsappAgent, { isLoading }] =
    integrationsApi.useDisconnectWhatsappAgentAccountMutation();

  const handleRemoveAccount = useCallback(async () => {
    const resp = await disconnectWhatsappAgent({ entityId });

    if (resp && "data" in resp) {
      openManageAccountDrawer(undefined);
      if (isInboxModule) {
        navigateToPage("clientSupport");
      }
      return;
    }

    toast(
      t({
        id: "feedback.something-went-wrong",
        defaultMessage: "Something went wrong",
      }),
      { variant: "error" }
    );
  }, [
    disconnectWhatsappAgent,
    entityId,
    isInboxModule,
    navigateToPage,
    openManageAccountDrawer,
    t,
    toast,
  ]);

  if (variant === "tile") {
    const handleClick = () => {
      if (!isClickable) {
        return;
      }
      if (isConnected) {
        transitionTo({ name: "whatsappAccount" });
      } else {
        connectWhatsappAccount().then(() => openManageAccountDrawer(undefined));
      }
    };
    return (
      <CRMAccountTile
        isConnected={isConnected}
        img={whatsappAgent?.profile[0]?.profile_picture_url}
        defaultAvatar={defaultAvatar}
        title={
          whatsappAgent?.name ??
          t({
            id: "client-support-page.whatsapp",
            defaultMessage: "WhatsApp",
          })
        }
        onClick={handleClick}
        icon={WhatsappBorderedIcon}
        description={
          isConnected
            ? `Account ID: ${whatsappAgent?.waba_id}  •  ${whatsappAgent?.phone_number.display_phone_number}`
            : t({
                id: "client-support-page.whatsapp-account-disconnected-2",
                defaultMessage:
                  "Reply faster on WhatsApp. Create messages templates that will be sent to your Clients automatically 🔥",
              })
        }
        status={isConnected ? "accountConnected" : "accountDisconnected"}
        menuItems={[
          [
            {
              id: "reconnect-account",
              label: t({
                id: "client-support-page.reconnect",
                defaultMessage: "Reconnect",
              }),
              onSelect: () =>
                reconnectWhatsappAccount().then(() =>
                  openManageAccountDrawer(undefined)
                ),
              isDisabled: isLoading,
              isHidden: hideReconnectOption || isConnected,
            },
            {
              id: "disconnect-account",
              label: t({
                id: "client-support-page.disconnect-account",
                defaultMessage: "Disconnect Account",
              }),
              icon: <RemoveIcon />,
              onSelect: handleRemoveAccount,
              isDisabled: isLoading,
            },
          ],
        ]}
      />
    );
  }

  if (!whatsappAgent) return null;
  return (
    <CRMAccountCard
      img={whatsappAgent.profile[0]?.profile_picture_url}
      title={whatsappAgent.name}
      buttonProps={
        !hideReconnectOption && !isConnected
          ? {
              text: t({
                id: "client-support-page.reconnect",
                defaultMessage: "Reconnect",
              }),
              onClick: () =>
                reconnectWhatsappAccount().then(() =>
                  openManageAccountDrawer(undefined)
                ),
            }
          : undefined
      }
      onClick={
        isClickable
          ? () => transitionTo({ name: "whatsappAccount" })
          : undefined
      }
      icon={WhatsappIcon}
      description={
        isConnected
          ? `Account ID: ${whatsappAgent.waba_id}  •  ${whatsappAgent.phone_number.display_phone_number}`
          : t({
              id: "client-support-page.whatsapp-account-disconnected",
              defaultMessage:
                "WhatsApp Account is disconnected. Please reconnect to continue using WhatsApp CRM",
            })
      }
      status={isConnected ? "accountConnected" : "accountDisconnected"}
      menuItems={[
        [
          {
            id: "disconnect-account",
            label: t({
              id: "client-support-page.disconnect-account",
              defaultMessage: "Disconnect Account",
            }),
            icon: <RemoveIcon />,
            onSelect: handleRemoveAccount,
            isDisabled: isLoading,
          },
        ],
      ]}
    />
  );
};
